import _ from 'lodash';

import { getSelectedSampleForNode } from 'app/beta-editor/utils/FlowUtils';
import { isFilter, isRead } from 'app/service/utils';
import { isPathNode, isPathsGroupNode } from 'app/beta-editor/utils/PathUtils';

import { FlowNode } from 'app/beta-editor/flowState';
import { RequiredBehaviors } from 'app/beta-editor/pages/types';
import { ServicesMap } from 'app/service/types';
import getSampleStatus from 'app/beta-editor/utils/getSampleStatus';
import isValidAuth from 'app/beta-editor/utils/isValidAuth';
import getSample from 'app/beta-editor/utils/getSample';
import isFilterWithEmptyCriteria from 'app/beta-editor/utils/isFilterWithEmptyCriteria';
import isValidSampleStatus from 'app/beta-editor/utils/isValidSampleStatus';

interface SampleBehavior extends RequiredBehaviors {
  isApplicable: (props: { node: FlowNode; services: ServicesMap }) => boolean;
}

const sampleBehavior: SampleBehavior = {
  label({ node, flow, isNodeWaiting }) {
    const sample = getSelectedSampleForNode(flow, node);
    const sampleStatus = getSampleStatus(node, flow);
    const isTriggerStep = isRead(node.type_of);
    const findOrSend = isTriggerStep ? 'Find' : 'Send';
    const hasDataAndNotWaiting =
      ['data'].includes(sampleStatus) && !isNodeWaiting;
    const didSkipSample = _.get(sample, ['_zap_data_was_skipped'], false);
    if (didSkipSample) {
      return `${findOrSend} Data (Skipped)`;
    } else if (sampleStatus === 'error') {
      return `${findOrSend} Data (Error)`;
    } else if (hasDataAndNotWaiting) {
      return `${findOrSend} Data`;
    } else {
      return `${findOrSend} Data`;
    }
  },

  menuLabel(props) {
    return sampleBehavior.label(props);
  },

  crumbLabel(props) {
    return sampleBehavior.label(props);
  },

  data() {
    return;
  },

  status({ node, flow, isNodeWaiting, nodeId }) {
    const sampleStatus = getSampleStatus(node, flow);
    // if we're currently on a trigger step, every zap status should react to the
    // the trigger's loading status. However if we're on any other step,
    // the node status should only react to that particular node's testing status.
    const isWaiting = node.id === nodeId ? isNodeWaiting : false;
    if (isWaiting || sampleStatus === 'waiting') {
      return 'waiting';
    } else if (isValidSampleStatus(node, sampleStatus)) {
      return 'success';
    } else if (sampleStatus === 'error') {
      return 'error';
    } else {
      return '';
    }
  },

  isApplicable(props) {
    const { node, services } = props;
    return Boolean(
      node.selected_api && node.action && isValidAuth(node, services)
    );
  },

  visible(props) {
    const { node } = props;
    return (
      !isFilter(node.type_of) && !isPathsGroupNode(node) && !isPathNode(node)
    );
  },

  isClickable({ node, flow }) {
    return (
      Boolean(getSample(node, flow.taskState)) &&
      !_.get(flow, ['nodeLoading', node.id])
    );
  },

  isLocked(props) {
    const { node } = props;
    const hasEmptyCriteria = isFilterWithEmptyCriteria(node);
    return !sampleBehavior.isApplicable(props) || hasEmptyCriteria;
  },
};

export default sampleBehavior;
